import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { BlogBlockParagraph } from '../blog.models';

@Component({
  selector: 'app-blog-block-paragraph',
  templateUrl: './block-paragraph.component.html',
  styleUrls: ['./block-paragraph.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BlogBlockParagraphComponent implements OnChanges {
  @Input({ required: true }) block!: BlogBlockParagraph;
  
  processedHtml: SafeHtml = '';

  constructor(private sanitizer: DomSanitizer) {}

  ngOnChanges(): void {
    const rawHtml = this.parseAndSanitize(this.block.html);
    this.processedHtml = rawHtml;
  }

  /**
   * Converts markdown-like text to HTML, supporting:
   * - Bullet lists (lines starting with "- ")
   * - Numbered lists (lines starting with "1. ", "2. ", etc.)
   * - Bold (**text** or __text__)
   * - Italic (*text* or _text_)
   * - Inline code (`code`)
   * - Links ([text](url))
   * 
   * If content already contains HTML tags (from Strapi v5 rich text editor),
   * it will be sanitized and returned as-is.
   */
  private parseAndSanitize(text: string): SafeHtml {
    if (!text) {
      return '';
    }

    // Check if content already contains HTML tags (Strapi v5 rich text format)
    const hasHtmlTags = /<(p|ul|ol|li|strong|em|h[1-6]|a|blockquote|code|pre)\b[^>]*>/i.test(text);
    
    if (hasHtmlTags) {
      // Content is already HTML from Strapi - just sanitize and return
      return this.sanitizer.sanitize(1, text) || '';
    }

    // Content is plain text/markdown - apply parser
    let html = text;

    // Split into lines to process lists
    const lines = html.split('\n');
    const processedLines: string[] = [];
    let inBulletList = false;
    let inNumberedList = false;
    let paragraphBuffer: string[] = [];

    const flushParagraphBuffer = () => {
      if (paragraphBuffer.length > 0) {
        const content = paragraphBuffer.join(' ').trim();
        if (content) {
          processedLines.push(`<p>${content}</p>`);
        }
        paragraphBuffer = [];
      }
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmedLine = line.trim();

      // Empty line - flush paragraph buffer
      if (!trimmedLine) {
        flushParagraphBuffer();
        continue;
      }

      // Check for bullet list item (- )
      const bulletMatch = trimmedLine.match(/^[-•]\s+(.+)$/);
      if (bulletMatch) {
        flushParagraphBuffer();
        if (!inBulletList) {
          processedLines.push('<ul>');
          inBulletList = true;
        }
        processedLines.push(`<li>${bulletMatch[1]}</li>`);
        continue;
      } else if (inBulletList) {
        processedLines.push('</ul>');
        inBulletList = false;
      }

      // Check for numbered list item (1. , 2. , etc.)
      const numberedMatch = trimmedLine.match(/^\d+\.\s+(.+)$/);
      if (numberedMatch) {
        flushParagraphBuffer();
        if (!inNumberedList) {
          processedLines.push('<ol>');
          inNumberedList = true;
        }
        processedLines.push(`<li>${numberedMatch[1]}</li>`);
        continue;
      } else if (inNumberedList) {
        processedLines.push('</ol>');
        inNumberedList = false;
      }

      // Regular line - add to paragraph buffer
      paragraphBuffer.push(trimmedLine);
    }

    // Flush any remaining paragraph content
    flushParagraphBuffer();

    // Close any open lists
    if (inBulletList) {
      processedLines.push('</ul>');
    }
    if (inNumberedList) {
      processedLines.push('</ol>');
    }

    html = processedLines.join('');

    // Process inline formatting
    // Bold: **text** or __text__
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');

    // Italic: *text* or _text_ (but not if part of **)
    html = html.replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>');
    html = html.replace(/(?<!_)_(?!_)(.+?)(?<!_)_(?!_)/g, '<em>$1</em>');

    // Inline code: `code`
    html = html.replace(/`([^`]+)`/g, '<code>$1</code>');

    // Links: [text](url)
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

    return this.sanitizer.sanitize(1, html) || '';
  }
}
